import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    showProjectPriorityMessage,
    onSaveProjectPrioritySuccess
} from 'actions/projectPriority';
import {
    onSaveProjectSuccess,
    onProjectPrioritiesDashboard,
    onGlobalProjectBusinessPrioritiesDashboard,
    onGlobalProjectCostPrioritiesDashboard
} from 'actions/project';

import {
    ON_SAVE_PROJECT_PRIORITY,
    ON_DELETE_PROJECT_PRIORITY,
    ON_UPDATE_PROJECT_PRIORITIES
} from 'constants/ActionTypes';
import { generateGuid } from 'services/Authentication';
import { addOrUpdateDataRequest, updateDataRequest } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { calculateWeight } from 'services/projectService';
import {
    ORGID_PATH_PARAM,
    PROJECT_BUS_PRIORITIES_UPDATE_ENDPOINT,
    PROJECT_COST_PRIORITIES_UPDATE_ENDPOINT,
    PROJECT_BUS_PRIORITIES_DELETE_ENDPOINT,
    PROJECT_COST_PRIORITIES_DELETE_ENDPOINT,
    PROJECT_ADD_ENDPOINT,
    PROJECT_UPDATE_ENDPOINT
} from 'constants/api';

const currentOrg = (state) => state.orgs.selectedOrg;
const getProjectList = (state) => state.projects.allProject;
const getSelectedProject = (state) => state.projects.selectedProject;
const getProjectPriorityList = (state) => state.projectPrioritys.allProjectPriority; // get all active and inactive
const updatingProjectCostPriorities = (state) => state.projectPrioritys.updatingProjectCostPriorities;
const updatingProjectBusinessPriorities = (state) => state.projectPrioritys.updatingProjectBusinessPriorities;
const updatingGlobalPrioritiesState = (state) => state.projectPrioritys.updatingGlobalPriorities;

/**
 * Per project - go through the param list being changed - then update all corresponding entries and then save the project as a whole
 * This will either update an existing entry (name and desc only) or add a new entry if it doesnt exist - used by the priority update and add priority
 * NOTE 1 - just pass the payload as the api does all the hard work (could be multiple updates so reduce the noise on the network)
 * NOTE 2 - the value of the priority cannot be changed so no need to recalculate the weight of the project
 * @param {*} payload {id, name, descrition, weight} 
 */
function* updateProjectPriorities({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const updatingBusinessPriorities = yield select(updatingProjectBusinessPriorities);

        // change the payload to include the orgid for security reasons - checks against the path param
        const newPayload = {
            orgid: selectedOrg ?.id,
            priority: { ...payload }
        };
        const auditLogMessage = updatingBusinessPriorities ? 'Updating business priorities name and description' : 'Updating cost priorities name and description';
        const auditLog = { message: auditLogMessage, name: payload.name, description: payload.description };
        const endpointConfig = updatingBusinessPriorities ? PROJECT_BUS_PRIORITIES_UPDATE_ENDPOINT : PROJECT_COST_PRIORITIES_UPDATE_ENDPOINT;
        const newProjectList = yield call(
            updateDataRequest,
            newPayload,
            endpointConfig,
            auditLog,
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        );

        // need to update the store with the updated priorities
        const selectedProject = newProjectList ?.length > 0 ? newProjectList[0] : [];
        const updatedPayload = (selectedProject && selectedProject ?.busValPriorityParams ?.length > 0) ? selectedProject.busValPriorityParams : [];
        yield put(onProjectPrioritiesDashboard(selectedProject));
        if (updatingBusinessPriorities) {
            yield put(onGlobalProjectBusinessPrioritiesDashboard(updatedPayload));
        } else {
            yield put(onGlobalProjectCostPrioritiesDashboard(updatedPayload));
        }
        yield put(onSaveProjectSuccess(newProjectList));
    } catch (error) {
        yield put(showProjectPriorityMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * delete will remove all corresponding entries in all projects - done via the api - Will get the updated payload to put back in the store
 * @param {*} param0 
 */
export function* deleteProjectPriority({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const updatingBusinessPriorities = yield select(updatingProjectBusinessPriorities);

        // change the payload to include the orgid for security reasons - checks against the path param
        const newPayload = {
            orgid: selectedOrg ?.id,
            priority: { ...payload }
        };
        const auditLogMessage = updatingBusinessPriorities ? 'Deleting business priorities by name match' : 'Deleting cost priorities by name match';
        const auditLog = { message: auditLogMessage, name: payload.name };
        const endpointConfig = updatingBusinessPriorities ? PROJECT_BUS_PRIORITIES_DELETE_ENDPOINT : PROJECT_COST_PRIORITIES_DELETE_ENDPOINT;
        const newProjectList = yield call(
            updateDataRequest,
            newPayload,
            endpointConfig,
            auditLog,
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        );

        // need to update the store with the updated priorities
        const selectedProject = newProjectList ?.length > 0 ? newProjectList[0] : [];
        const updatedPayload = (selectedProject && selectedProject ?.busValPriorityParams ?.length > 0) ? selectedProject.busValPriorityParams : [];
        yield put(onProjectPrioritiesDashboard(selectedProject));
        if (updatingBusinessPriorities) {
            yield put(onGlobalProjectBusinessPrioritiesDashboard(updatedPayload));
        } else {
            yield put(onGlobalProjectCostPrioritiesDashboard(updatedPayload));
        }
        yield put(onSaveProjectSuccess(newProjectList));
    } catch (error) {
        yield put(showProjectPriorityMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveProjectPriority({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const projectList = yield select(getProjectList);
        const selectedProject = yield select(getSelectedProject);
        const allProjectPrioritys = yield select(getProjectPriorityList);
        const updatingCostPriorities = yield select(updatingProjectCostPriorities);
        const updatingBusinessPriorities = yield select(updatingProjectBusinessPriorities);
        const updatingGlobalPriorities = yield select(updatingGlobalPrioritiesState);
        const updatedProject = yield call(saveProjectPriorityRequest,
            selectedProject,
            allProjectPrioritys,
            updatingCostPriorities,
            updatingBusinessPriorities,
            updatingGlobalPriorities,
            payload
        );
        const savedProject = yield call(
            addOrUpdateDataRequest,
            projectList,
            updatedProject,
            PROJECT_ADD_ENDPOINT,
            PROJECT_UPDATE_ENDPOINT,
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        );
        yield put(onSaveProjectSuccess(savedProject));
        yield put(onSaveProjectPrioritySuccess(updatingCostPriorities ? updatedProject ?.costImplPriorityParams : updatedProject ?.busValPriorityParams));
    } catch (error) {
        yield put(showProjectPriorityMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This simply sets up the selectedProject with the new or edited priority - then call saveProject to do the actual call
 * @param {*} selectedProject 
 * @param {*} allProjectPrioritys 
 * @param {*} updatingCostPriorities 
 * @param {*} updatingBusinessPriorities
 * @param {*} updatingGlobalPriorities 
 * @param {*} payload 
 */
export const saveProjectPriorityRequest = async (selectedProject,
    allProjectPrioritys,
    updatingCostPriorities,
    updatingBusinessPriorities,
    updatingGlobalPriorities,
    payload
) => {
    let isNew = true;
    let updatedProject = {};
    const projectPriorityList = allProjectPrioritys.map((projectPriority) => {
        if (projectPriority.id === payload.id) {
            isNew = false;
            return payload;
        } else {
            return projectPriority;
        }
    });
    // payload is not in the list if the id is empty so add an id (not added by the api call) and add to the list
    if (isNew) {
        const generatedId = generateGuid();
        const updatedPayload = { ...payload, id: generatedId };
        projectPriorityList.push(updatedPayload);
    }
    // now update the correct params array object in the selectedProject object
    if (updatingCostPriorities) {
        updatedProject = { ...selectedProject, costImplPriorityParams: projectPriorityList };
    } else if (updatingBusinessPriorities) {
        updatedProject = { ...selectedProject, busValPriorityParams: projectPriorityList };
    }
    // TODO api should recalc the weight on save project - used by delete priority which is done on the server
    const updatedProjectWeightRecalculated = calculateWeight(updatedProject);
    return updatedProjectWeightRecalculated;
};

export function* saveProjectPrioritySaga() {
    yield takeEvery(ON_SAVE_PROJECT_PRIORITY, saveProjectPriority);
}

export function* deleteProjectPrioritySaga() {
    yield takeEvery(ON_DELETE_PROJECT_PRIORITY, deleteProjectPriority);
}

export function* updateProjectPrioritiesSaga() {
    yield takeEvery(ON_UPDATE_PROJECT_PRIORITIES, updateProjectPriorities);
}

export default function* rootSaga() {
    yield all([
        fork(saveProjectPrioritySaga),
        fork(deleteProjectPrioritySaga),
        fork(updateProjectPrioritiesSaga)
    ]);
}


// WEBPACK FOOTER //
// ./sagas/projectPriority.js